import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import { toFiling } from "@/queries/filings"
import type { ApiPage, ApiResource, Paginated } from "@/lib/api/types"
import type {
  ApiReview,
  Review,
  ReviewFilters,
  ReviewPreview,
  SendReviewInput,
} from "@/types/review"

export function toReview(data: ApiReview): Review {
  return {
    id: data.id,
    type: data.type,
    typeLabel: data.type_label,
    title: data.title,
    status: data.status,
    outcome: data.outcome,
    standing: data.standing,
    badge: data.badge,
    firm: data.firm
      ? { id: data.firm.id, legalName: data.firm.legal_name }
      : null,
    subject: data.subject ?? null,
    progressLabel: data.progress?.label ?? null,
    sendOn: data.send_on,
    sentAt: data.sent_at,
    dueOn: data.due_on,
    approvalsLabel: data.approvals_label ?? "",
    canApprove: data.can_approve ?? false,
    approvalStatement: data.approval_statement ?? null,
    earlyReason: data.early_reason,
    snapshot: data.snapshot
      ? { id: data.snapshot.id, takenAt: data.snapshot.taken_at }
      : null,
    back: data.back ?? null,
    pages: data.pages ?? [],
    parts: data.parts ?? [],
    overview: data.overview ?? null,
    notices: data.notices ?? [],
    facts: data.facts ?? null,
    approvers: data.approvers ?? [],
    approversEmpty: data.approvers_empty ?? null,
    recipients: data.recipients ?? [],
    steps: data.steps ?? [],
    changes: data.changes ?? [],
    issues: (data.issues ?? []).map((issue) => ({
      id: issue.id,
      title: issue.title,
    })),
    emails: (data.emails ?? []).map((email) => ({
      sentAt: email.sent_at,
      label: email.label,
    })),
    actions: data.actions ?? [],
    filings: (data.filings ?? []).map(toFiling),
    aiSummary: data.ai_summary,
    aiSummarisedAt: data.ai_summarised_at,
    createdAt: data.created_at,
    approvedAt: data.approved_at,
  }
}

export const reviewQueries = {
  list: (filters: ReviewFilters = {}) =>
    queryOptions({
      queryKey: apiKey("/reviews", filters),
      queryFn: async ({ signal }): Promise<Paginated<Review>> => {
        const response = await apiFetch<ApiPage<ApiReview>>("/reviews", {
          params: {
            status: filters.status,
            individual_id: filters.individual_id,
            firm_id: filters.firm_id,
            awaiting: filters.awaiting,
            page: filters.page,
          },
          signal,
        })

        return {
          items: response.data.map(toReview),
          total: response.meta.total,
          currentPage: response.meta.current_page,
          lastPage: response.meta.last_page,
        }
      },
    }),

  preview: (input: SendReviewInput) =>
    queryOptions({
      queryKey: [...apiKey("/reviews", input), "preview"],
      queryFn: async ({ signal }) =>
        (
          await apiFetch<ApiResource<ReviewPreview>>("/reviews", {
            method: "POST",
            body: { ...input, preview: true },
            signal,
          })
        ).data,
    }),

  detail: (reviewId: string) =>
    queryOptions({
      queryKey: apiKey(`/reviews/${reviewId}`),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiReview>>(
          `/reviews/${reviewId}`,
          { signal }
        )

        return toReview(data)
      },
    }),
}

export function useAddReviewStep(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (section: string) => {
      const { data } = await apiFetch<ApiResource<ApiReview>>(
        `/reviews/${reviewId}/steps`,
        { method: "POST", body: { section } }
      )

      return toReview(data)
    },
    onSuccess: (review) => {
      queryClient.setQueryData(apiKey(`/reviews/${review.id}`), review)
    },
  })
}

export function useApproveReview(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (comment: string) => {
      const { data } = await apiFetch<ApiResource<ApiReview>>(
        `/reviews/${reviewId}/approvals`,
        {
          method: "POST",
          body: comment === "" ? { agreed: true } : { agreed: true, comment },
        }
      )

      return toReview(data)
    },
    onSuccess: (review) => {
      queryClient.setQueryData(apiKey(`/reviews/${review.id}`), review)
    },
  })
}

export function useSendReview() {
  return useMutation({
    mutationFn: async (input: SendReviewInput) =>
      (
        await apiFetch<{ data: ApiReview[] }>("/reviews", {
          method: "POST",
          body: input,
        })
      ).data.map(toReview),
  })
}

export function useResendReview(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiFetch<ApiResource<ApiReview>>(
        `/reviews/${reviewId}/emails`,
        { method: "POST" }
      )

      return toReview(data)
    },
    onSuccess: (review) => {
      queryClient.setQueryData(apiKey(`/reviews/${review.id}`), review)
    },
  })
}

export function useUpdateReview(reviewId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input:
        | { send_on: string }
        | { due_on: string }
        | { status: "closed"; reason: string }
    ) => {
      const { data } = await apiFetch<ApiResource<ApiReview>>(
        `/reviews/${reviewId}`,
        { method: "PATCH", body: input }
      )

      return toReview(data)
    },
    onSuccess: (review) => {
      queryClient.setQueryData(apiKey(`/reviews/${review.id}`), review)
    },
  })
}
